import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as Icons from 'react-feather'
import DataTable from 'react-data-table-component'
import axios from 'axios'
import PageHeader from '../../shared/page-header/PageHeader'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg'
import { exportToExcel, exportToPDF } from '../../utils/downloadExportedFiles'
import { fetchSalesPendingPaymentList } from '../../utils/salesRegisterApi'
import { getStorageValues } from '../../shared/CommonFunctions'
import { getApiBaseUrl } from '../../utils/apiConfig'
import PaymentFollowupRowFollowup from './PaymentFollowupRowFollowup'
import { Link, useLocation, useNavigate } from 'react-router-dom'

function PaymentFollowupMain(props) {
  const [alert, setAlert] = useState(null)
  const [paymentFollowup, setPaymentFollowup] = useState([])
  const [filterText, setFilterText] = useState('')
  const [receiptDrafts, setReceiptDrafts] = useState({})
  const [savingRowId, setSavingRowId] = useState(null)
  const [expandedRowId, setExpandedRowId] = useState(null)
  const followupRef = useRef(null)
  const userId = useMemo(() => getStorageValues('userId'), [])
  const location = useLocation()
  const navigate = useNavigate()

  const formatCurrency = (amount) => {
    const value = Number(amount) || 0
    try {
      return value.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })
    } catch (_) {
      return `₹${value.toFixed(2)}`
    }
  }

  const fetchPaymentFollowup = async () => {
    try {
      const rows = await fetchSalesPendingPaymentList({ userId })
      setPaymentFollowup(Array.isArray(rows) ? rows : [])
    } catch (error) {
      console.log(error)
      CreateAlert('Unable to load pending payment list', 'Error', setAlert)
    }
  }

  useEffect(() => {
    fetchPaymentFollowup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const defaultId = location?.state?.DefaultSalesRegisterId
    const expand = location?.state?.ExpandFollowup
    if (defaultId && (expand === undefined || Boolean(expand))) {
      setExpandedRowId(defaultId)
      navigate(location.pathname, { replace: true, state: {} })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.DefaultSalesRegisterId, location?.state?.ExpandFollowup])

  const handleExportExcel = () => {
    const columns = [
      { name: 'Sales #', selector: 'SalesNumber' },
      { name: 'Customer', selector: 'CustomerName' },
      { name: 'Mobile', selector: 'MobileNumber' },
      { name: 'Discount', selector: 'DiscountAmount' },
      { name: 'Cash Total', selector: 'CashTotal' },
      { name: 'Pending', selector: 'PendingAmount' }
    ]
    exportToExcel('PaymentFollowup', columns, paymentFollowup)
  }

  const handleExportPDF = () => {
    const columns = [
      { name: 'Sales #', selector: 'SalesNumber' },
      { name: 'Customer', selector: 'CustomerName' },
      { name: 'Mobile', selector: 'MobileNumber' },
      { name: 'Discount', selector: 'DiscountAmount' },
      { name: 'Cash Total', selector: 'CashTotal' },
      { name: 'Pending', selector: 'PendingAmount' }
    ]
    exportToPDF('PaymentFollowup', columns, paymentFollowup)
  }

  const rows = (paymentFollowup || []).filter((item) => {
    const term = filterText.toLowerCase()
    return (
      (item.SalesNumber && item.SalesNumber.toLowerCase().includes(term)) ||
      (item.CustomerName && item.CustomerName.toLowerCase().includes(term)) ||
      (item.MobileNumber && item.MobileNumber.toLowerCase().includes(term))
    )
  })

  const updateDraft = (salesRegisterId, patch) => {
    setReceiptDrafts((prev) => ({
      ...prev,
      [salesRegisterId]: { ...(prev?.[salesRegisterId] || {}), ...(patch || {}) }
    }))
  }

  const saveReceipt = async (row) => {
    const salesRegisterId = row?.SalesRegisterId
    if (!salesRegisterId) return

    const raw = String(receiptDrafts?.[salesRegisterId]?.amount ?? '').replace(/,/g, '').trim()
    const amount = Number(raw)
    if (!raw || !Number.isFinite(amount) || amount <= 0) {
      CreateAlert('Please enter a valid receipt amount', 'Warning', setAlert)
      return
    }
    const remarks = String(receiptDrafts?.[salesRegisterId]?.remarks ?? '').trim() || null

    setSavingRowId(salesRegisterId)
    try {
      const res = await axios.post(`${getApiBaseUrl()}/salesregister/savePaymentReceipt`, {
        UserId: userId,
        SalesRegisterId: salesRegisterId,
        Amount: amount,
        Remarks: remarks
      })
      if (res.status === 200) {
        const ok = res?.data?.success ?? true
        if (!ok) {
          CreateAlert(res?.data?.message || 'Unable to save receipt', 'Warning', setAlert)
          return
        }
        CreateAlert(res?.data?.message || 'Receipt saved', 'Success', setAlert)
        updateDraft(salesRegisterId, { amount: '', remarks: '' })
        await fetchPaymentFollowup()
      }
    } catch (error) {
      console.log(error)
      CreateAlert(error?.response?.data?.message || 'Unable to save receipt', 'Error', setAlert)
    } finally {
      setSavingRowId(null)
    }
  }

  const columns = [
    {
      name: <div className="titlename"><span>Sales #</span></div>,
      selector: (row) => row.SalesNumber,
      cell: (row) => <span title={row.SalesNumber}>{row.SalesNumber}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
      width: '180px'
    },
    {
      name: <div className="titlename"><span>Customer</span></div>,
      selector: (row) => row.CustomerName,
      cell: (row) => <span title={row.CustomerName}>{row.CustomerName || '-'}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
      width: '220px'
    },
    {
      name: <div className="titlename"><span>Mobile</span></div>,
      selector: (row) => row.MobileNumber,
      cell: (row) => <span title={row.MobileNumber}>{row.MobileNumber || '-'}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
      width: '140px'
    },
    {
      name: <div className="titlename"><span>Cash Total</span></div>,
      selector: (row) => row.CashTotal,
      cell: (row) => <span title={String(row.CashTotal)}>{formatCurrency(row.CashTotal)}</span>,
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Discount</span></div>,
      selector: (row) => row.DiscountAmount,
      cell: (row) => <span title={String(row.DiscountAmount)}>{formatCurrency(row.DiscountAmount)}</span>,
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Pending</span></div>,
      selector: (row) => row.PendingAmount,
      cell: (row) => <span title={String(row.PendingAmount)}>{formatCurrency(row.PendingAmount)}</span>,
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Receipt Amount</span></div>,
      cell: (row) => {
        const salesRegisterId = row?.SalesRegisterId
        const value = receiptDrafts?.[salesRegisterId]?.amount ?? ''
        return (
          <input
            type="text"
            className="form-control form-control-sm"
            value={value}
            onChange={(e) => updateDraft(salesRegisterId, { amount: e.target.value })}
            style={{ textAlign: 'right', minWidth: '120px' }}
            placeholder="0.00"
          />
        )
      },
      width: '180px'
    },
    {
      name: <div className="titlename"><span>Remark</span></div>,
      cell: (row) => {
        const salesRegisterId = row?.SalesRegisterId
        const value = receiptDrafts?.[salesRegisterId]?.remarks ?? ''
        return (
          <input
            type="text"
            className="form-control form-control-sm"
            value={value}
            onChange={(e) => updateDraft(salesRegisterId, { remarks: e.target.value })}
            style={{ minWidth: '180px' }}
            placeholder="Remark"
          />
        )
      },
      width: '220px'
    },
    {
      name: <div className="titlename">Action</div>,
      cell: (row) => {
        const salesRegisterId = row?.SalesRegisterId
        const disabled = savingRowId === salesRegisterId
        return (
          <div className="edit-delete-action">
            <Link
              to=""
              className="me-2 p-2"
              style={{
                display: 'inline-flex',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                alignItems: 'center',
              }}
              title={disabled ? 'Saving…' : 'Save receipt'}
              onClick={(e) => {
                e.preventDefault()
                if (!disabled) saveReceipt(row)
              }}
            >
              <Icons.Save size={14} data-feather="save" className="feather-save" />
            </Link>
          </div>
        )
      },
      width: '90px'
    }
  ]

  const handleRowExpandToggled = (expanded, row) => {
    setExpandedRowId((prev) => (prev === row.SalesRegisterId ? null : row.SalesRegisterId))
  }

  const isRowExpanded = (row) => row.SalesRegisterId === expandedRowId

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        onRefresh={fetchPaymentFollowup}
        showPDF={true}
        showExcel={true}
        showPrint={false}
        showRefresh={true}
        onExcelExport={handleExportExcel}
        onPdfExport={handleExportPDF}
      />

      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>

          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="SalesNumber"
            sortIcon={<SortArrow />}
            persistTableHead={true}
            expandableRows
            onRowExpandToggled={handleRowExpandToggled}
            expandableRowExpanded={isRowExpanded}
            expandableRowsComponent={PaymentFollowupRowFollowup}
            expandableRowsComponentProps={{ ref: followupRef, onReceiptChanged: fetchPaymentFollowup }}
          />
        </div>
      </div>
    </>
  )
}

export default PaymentFollowupMain